import { Container, Loader2 } from 'lucide-react'
import { useNowTick } from '@/hooks/useNowTick'
import { formatElapsedTime } from '@/lib/timeFormatters'

const LONG_RUNNING_EFFORTS = new Set(['high', 'max'])
const SLOW_AFTER_SECONDS = 60

interface ResponseWaitNoticeProps {
  label: string
  isCreatingContainer: boolean
  startedAt: string
  effort: string | null
  effortLabel: string | null
}

function secondsSince(timestamp: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000))
}

export function ResponseWaitNotice({
  label,
  isCreatingContainer,
  startedAt,
  effort,
  effortLabel,
}: Readonly<ResponseWaitNoticeProps>) {
  useNowTick(true)

  const isLongRunningEffort = effort !== null && LONG_RUNNING_EFFORTS.has(effort)
  const isSlow = secondsSince(startedAt) >= SLOW_AFTER_SECONDS

  return (
    <div className="flex flex-col gap-1 py-2 text-muted-foreground" data-testid="response-wait-notice">
      <div className="flex items-center gap-2 text-sm">
        <span role="status" aria-live="polite" className="inline-flex items-center gap-2" data-testid="response-wait-status">
          {isCreatingContainer
            ? <Container className="h-4 w-4 animate-pulse" aria-hidden="true" />
            : <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          <span>{label}</span>
        </span>
        <span aria-hidden="true">·</span>
        <span role="timer" aria-label="Time elapsed" className="text-xs">
          {formatElapsedTime(startedAt, null)}
        </span>
      </div>
      {isLongRunningEffort && (
        <span className="text-xs" data-testid="response-wait-hint">
          {effortLabel ?? effort} effort can take several minutes.
        </span>
      )}
      {!isLongRunningEffort && isSlow && (
        <span className="text-xs" data-testid="response-wait-slow">This is taking longer than usual.</span>
      )}
    </div>
  )
}
